import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, FormControl, InputLabel, Select, MenuItem,
  TextField, Alert, CircularProgress
} from '@mui/material';
import { authService } from '../../services/authService';

export default function RoleAssignmentDialog({ open, onClose, userId, onSuccess }) {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [orgEntityId, setOrgEntityId] = useState('');
  const [facilityEntityIds, setFacilityEntityIds] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      loadRoles();
      setSelectedRole('');
      setOrgEntityId('');
      setFacilityEntityIds('');
      setError(null);
    }
  }, [open]);

  const loadRoles = async () => {
    setLoadingRoles(true);
    try {
      const data = await authService.listRoles();
      setRoles(data ?? []);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.response?.data?.error || err.message);
    } finally {
      setLoadingRoles(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedRole) {
      setError('Please select a role');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const facilityIds = facilityEntityIds
        ? facilityEntityIds.split(',').map(s => s.trim()).filter(Boolean)
        : undefined;
      await authService.assignRole(userId, {
        roleId: selectedRole,
        orgEntityId: orgEntityId || undefined,
        facilityEntityIds: facilityIds
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error?.message || err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign Role</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{typeof error === 'string' ? error : error?.message || JSON.stringify(error)}</Alert>}

        {loadingRoles ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                label="Role"
              >
                {roles.map((role) => (
                  <MenuItem key={role.role_id} value={role.role_id}>
                    {role.role_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Organization Entity ID (optional)"
              value={orgEntityId}
              onChange={(e) => setOrgEntityId(e.target.value)}
              helperText="Scope this role to a specific organization"
              margin="normal"
              size="small"
            />

            <TextField
              fullWidth
              label="Facility Entity IDs (optional, comma-separated)"
              value={facilityEntityIds}
              onChange={(e) => setFacilityEntityIds(e.target.value)}
              helperText="Scope this role to specific facilities"
              margin="normal"
              size="small"
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading || loadingRoles}>
          {loading ? <CircularProgress size={20} /> : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
